/**
 * @fileoverview Connect an assigned Inbox task to explicit version-checked payment/identity review decisions. The backend owns assignment, revision, evidence requirements, and task transitions. This panel owns staff intent and feedback. Accepting a review is separate from moving funds or acknowledging external notification delivery.
 */

import React, { useState } from 'react';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import { formatDate } from '../Intake/intakeFormatters';
import './PaymentReviewPanel.css';



export default function PaymentReviewPanel({ task, onTaskChanged, assignmentManaged = false, canReview = true }) {
  const review = task.paymentReview;
  const submission = review.submission;
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const isAccounting = task.typeKey === 'prepare_payment_authorization';
  const canDecide = !isAccounting && task.status === 'open' && ['pending', 'held'].includes(review.status);

  const act = 

  async (decision) => {
    setBusy(true);
    setError('');
    try {
      if (decision === 'take') {
        await api.post(`/tasks/${task.id}/take`);
      } else {
        await api.post(`/tasks/${task.id}/payment-review`, {
          revision: review.revision,
          decision,
          reason: reason.trim(),
          ...(decision === 'accept' ? { evidence_reference: evidence.trim() } : {}),
        });
      }
      await onTaskChanged(task.id);
      setNotice(decision === 'take' ? 'Review assigned to you.' : 'Decision saved.');
    } catch (failure) {
      setError(failure?.response?.data?.error || 'Unable to save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!submission) return <p className="payment-review">Waiting for client submission.</p>;
  return (
    <section className="payment-review" aria-label="Payment instruction review">
      <div className="payment-review__status" role="status">
        <span><AppIcon icon={review.status === 'held' ? 'pi pi-pause-circle' : 'pi pi-shield'} aria-hidden="true" />
          {({ pending: 'Needs identity review', held: 'Held for follow-up', accepted: 'Identity accepted', rejected: 'Rejected' })[review.status] || review.status}
        </span>
        {review.test_mode ? <span className="payment-review__sandbox">Sandbox · simulated identity</span> : null}
      </div>

      <div className="payment-review__comparison">
        <section aria-label="Expected client">
          <h3>Client on file</h3>
          <strong>{review.expected_identity.name}</strong>
          <p>{review.expected_identity.email || 'No email on file'}</p>
          <p>{review.expected_identity.phone || 'No phone on file'}</p>
        </section>
        <section aria-label="Verified identity">
          <h3>Identity returned by Stripe</h3>
          <strong>{submission.identity.verified_name}</strong>
          <p><AppIcon icon="pi pi-check-circle" aria-hidden="true" /> ID check passed</p>
          <p><AppIcon icon="pi pi-check-circle" aria-hidden="true" /> Selfie matched ID</p>
        </section>
      </div>
      <section className="payment-review__destination" aria-label="Submitted instructions">
        <h3>Submitted instructions</h3>
        <dl>
          <div><dt>Bank</dt><dd>{submission.destination.bank_name}</dd></div>
          <div><dt>Account holder</dt><dd>{submission.destination.account_holder}</dd></div>
          <div><dt>Account</dt><dd>{submission.destination.account_type} · •••• {submission.destination.account_last4}</dd></div>
          <div><dt>Payment</dt><dd>USD · ACH · Amount not yet authorized</dd></div>
          <div><dt>Client confirmation</dt><dd>{formatDate(submission.submitted_at)}</dd></div>
          <div><dt>Receipt</dt><dd>{submission.receipt}</dd></div>
        </dl>
      </section>
      {review.decision_reason ? (
        <div className="payment-review__decision-note">
          <strong>{review.status === 'held' ? 'Follow-up needed' : 'Review decision'}</strong>
          <p>{review.decision_reason}</p>
          {review.evidence_reference ? <p>Evidence: {review.evidence_reference}</p> : null}
        </div>
      ) : null}
      {isAccounting ? (
        <div className="payment-review__decision-note">
          <h3>Payment authorization required</h3>
          <p>The client has confirmed a destination. Confirm the amount through a new client authorization before preparing payment.</p>
          <p>Payment preparation and release are not enabled yet.</p>
        </div>
      ) : null}
      {canDecide ? (
        <div className="payment-review__actions">
          {!assignmentManaged ? <div className="payment-review__owner">
            <span>Reviewer: <strong>{task.taskOwner?.name || task.taskOwner?.email || 'Unassigned'}</strong></span>
            <button className="task-inbox__button" type="button" disabled={busy} onClick={() => act('take')}>Take review</button>
          </div> : null}
          <p>Compare against independent matter records. A matching name alone does not establish identity.</p>
          <label htmlFor="payment-review-evidence">Evidence reference</label>
          <input id="payment-review-evidence" value={evidence} maxLength={500} onChange={(event) => setEvidence(event.target.value)} placeholder="Prior identity record or documented verification" />
          <label htmlFor="payment-review-reason">Review notes / follow-up reason</label>
          <textarea id="payment-review-reason" value={reason} maxLength={1500} rows={3} onChange={(event) => setReason(event.target.value)} />
          <label className="payment-review__confirmation">
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
            <span>I confirmed that the verified person is the client using independent evidence.</span>
          </label>
          <div className="payment-review__buttons">
            <button className="task-inbox__button task-inbox__button--primary" type="button" disabled={busy || !canReview || !reason.trim() || !evidence.trim() || !confirmed || !task.taskOwner} onClick={() => act('accept')}>Accept identity</button>
            <button className="task-inbox__button" type="button" disabled={busy || !canReview || !reason.trim() || !task.taskOwner} onClick={() => act('hold')}>Hold for follow-up</button>
            <button className="task-inbox__button" type="button" disabled={busy || !canReview || !reason.trim() || !task.taskOwner} onClick={() => act('reject')}>Reject</button>
          </div>
        </div>
      ) : null}
      {error ? <p role="alert" className="payment-review__error">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
    </section>
  );
}
